import React, { useEffect, useState, useCallback } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';

const containerStyle = {
  width: '100%',
  height: '300px',
};

const defaultCenter = {
  lat: 36.114647, // Luxor Hotel default fallback
  lng: -115.172813,
};

interface PickupMapProps {
  address: string;
  onAddressSelect: (address: string) => void;
  onCoordinatesSelect: (coords: { lat: number; lng: number }) => void;
  onOutOfRangeChange: (isOut: boolean) => void;
  origin?: google.maps.LatLngLiteral;
  radiusInMiles?: number;
}

const PickupMap: React.FC<PickupMapProps> = ({
  address,
  onAddressSelect,
  onCoordinatesSelect,
  onOutOfRangeChange,
  origin = defaultCenter,
  radiusInMiles = 5,
}) => {
  const [center, setCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyAC02KeKSJWJ6ieeoeKmbiLQSTdHqDq43c',
    libraries: ['places', 'geometry'],
  });

  // ✅ Compute distance and set out-of-range status when address changes
  useEffect(() => {
    if (address && isLoaded && address.length > 5) {
      const geocode = new google.maps.Geocoder();
      geocode.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          const latLng = { lat: location.lat(), lng: location.lng() };

          const selectedLatLng = new google.maps.LatLng(latLng.lat, latLng.lng);
          const originLatLng = new google.maps.LatLng(origin.lat, origin.lng);
          const distanceMeters = google.maps.geometry.spherical.computeDistanceBetween(
            originLatLng,
            selectedLatLng,
          );
          const distanceMiles = distanceMeters / 1609.34;

          console.log('[PickupMap] Distance (miles):', distanceMiles);

          setCenter(latLng);
          setMarkerPosition(latLng);
          onCoordinatesSelect(latLng);

          // only mark out of range if it came from full resolution
          onOutOfRangeChange(distanceMiles > radiusInMiles);
        } else {
          // Do NOT mark as out of range while typing — just clear marker
          setMarkerPosition(null);
        }
      });
    } else {
      setMarkerPosition(null);
    }
  }, [address, isLoaded, onCoordinatesSelect, onOutOfRangeChange, origin, radiusInMiles]);

  // ✅ Clicking the map also triggers distance validation
  const handleMapClick = useCallback(
    async (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;

      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const coords = { lat, lng };

      const selectedLatLng = new google.maps.LatLng(lat, lng);
      const originLatLng = new google.maps.LatLng(origin.lat, origin.lng);
      const distanceMeters = google.maps.geometry.spherical.computeDistanceBetween(
        originLatLng,
        selectedLatLng,
      );
      const distanceMiles = distanceMeters / 1609.34;

      console.log('[PickupMap] Click Distance (miles):', distanceMiles);

      if (distanceMiles > radiusInMiles) {
        onOutOfRangeChange(true);
        return;
      } else {
        onOutOfRangeChange(false);
      }

      setMarkerPosition(coords);
      setCenter(coords);
      onCoordinatesSelect(coords);

      const geocode = new google.maps.Geocoder();
      const response = await geocode.geocode({ location: coords });
      if (response.results && response.results.length > 0) {
        const formatted = response.results[0].formatted_address;
        onAddressSelect(formatted);
      }
    },
    [origin, radiusInMiles, onAddressSelect, onCoordinatesSelect, onOutOfRangeChange],
  );

  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={14}
      onClick={handleMapClick}
    >
      {markerPosition && <Marker position={markerPosition} />}
    </GoogleMap>
  );
};

export default PickupMap;
